import { useCallback, useEffect, useRef, useState } from 'react'

import { useHealthRepository } from '@/app/AppProviders'
import type { TodaySnapshot } from '@/domain/health/selectors'
import { sleepScore } from '@/domain/health/sleepScore'

interface TodaySnapshotState {
  snapshot: TodaySnapshot | null
  score: ReturnType<typeof sleepScore>
  error: Error | null
  reload(): void
}

function normalizeError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error('今日数据读取失败')
}

export function useTodaySnapshot(referenceAt: string, timeZone: string): TodaySnapshotState {
  const repository = useHealthRepository()
  const [result, setResult] = useState<{ key: string; snapshot: TodaySnapshot; score: ReturnType<typeof sleepScore> } | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [reloadVersion, setReloadVersion] = useState(0)
  const operationRef = useRef(0)
  const requestKey = `${referenceAt}\u0000${timeZone}\u0000${reloadVersion}`

  const reload = useCallback(() => {
    setReloadVersion((version) => version + 1)
  }, [])

  useEffect(() => {
    let active = true
    const operation = ++operationRef.current
    void Promise.all([
      repository.getToday(referenceAt, timeZone),
      repository.listSleepHistory(),
    ]).then(
      ([next, history]) => {
        if (!active || operation !== operationRef.current) return
        setError(null)
        setResult({ key: requestKey, snapshot: next, score: next.sleep ? sleepScore(next.sleep, history) : null })
      },
      (cause: unknown) => {
        if (!active || operation !== operationRef.current) return
        setError(normalizeError(cause))
      },
    )
    return () => {
      active = false
      operationRef.current += 1
    }
  }, [referenceAt, reloadVersion, repository, requestKey, timeZone])

  return {
    snapshot: result?.key === requestKey ? result.snapshot : null,
    score: result?.key === requestKey ? result.score : null,
    error,
    reload,
  }
}
